// SharePlanner — "share this listing on these portals, on these days" (owner 2026-10-09).
// Server: server/lib/share_plans.js (core 3135). Each platform × day is a run the server sends to
// the agent's PC as the same job a phone press makes, at an exact time or a random one inside a
// window — and only while that PC is online and logged in to the account confirmed HERE.
// ⚠️ "Phone numbers are the key": a platform can only be planned after the agent confirms the
// account the PC is logged in with (name + last phone digits). That account is stored with the
// plan; a different account on the PC at run time makes the run wait, never post.
// Everything the share will need is asked NOW, because nobody answers at run time: the portal's
// required fields (missingFor / renderField from ShareTab, shown as soon as a portal is ticked)
// and whether to pay the portal's fee from balance (payload.autoPay; the extension honours it
// for this job only; ss always asks 0.10₾, myhome after 3 free ads a month).
import { useEffect, useState } from 'react'
import { t } from '../../lib/i18n.js'
import { btn, input } from './primitives.jsx'
import { createSharePlan, getSharePlans, cancelSharePlan } from '../../db/core.js'

const PORTALS = [
  { key: 'ss', label: 'SS.ge' },
  { key: 'myhome', label: 'MyHome' },
  { key: 'korter', label: 'Korter' },
]
const tbilisiToday = () => new Date(Date.now() + 4 * 3600 * 1000).toISOString().slice(0, 10)
const addDays = (d, n) => new Date(Date.parse(d + 'T00:00:00Z') + n * 86400000).toISOString().slice(0, 10)
// The shared `input` style, but 16px on phones: iOS zooms the page into any smaller focused box.
const field = input.replace('text-sm', 'text-base sm:text-sm') + ' mt-1'
// A plan is live while any run can still go out; status stays 'active' after the last run ends.
const isLive = pl => pl.status === 'active' && (pl.share_plan_run || []).some(r => ['scheduled', 'queued'].includes(r.state))
const fmt = iso => new Date(iso).toLocaleString('ka-GE', { timeZone: 'Asia/Tbilisi', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

export function SharePlanner({ listingId, propertyId, outbound, device, missingFor, renderField, blocksFor = () => [] }) {
  const [platforms, setPlatforms] = useState(new Set())
  const [confirmed, setConfirmed] = useState(new Set())
  const [start, setStart] = useState(tbilisiToday())
  const [every, setEvery] = useState(1)
  const [end, setEnd] = useState(tbilisiToday())     // owner 2026-10-21: a date RANGE (start → end), not a count
  const [mode, setMode] = useState('window')
  const [from, setFrom] = useState('10:00')
  const [to, setTo] = useState('19:00')
  const [exact, setExact] = useState('10:00')
  const [plans, setPlans] = useState([])
  const [msg, setMsg] = useState(null)
  const [busy, setBusy] = useState(false)
  // Collapsed by default (owner 2026-10-09): most shares are sent now; the planner opens on demand.
  const [open, setOpen] = useState(false)
  const [autoPay, setAutoPay] = useState(null)        // null = not answered yet

  const load = () => listingId && getSharePlans(listingId).then(r => setPlans(r?.plans || [])).catch(() => {})
  useEffect(() => { load() }, [listingId])   // eslint-disable-line react-hooks/exhaustive-deps

  // What the ticked portals still need, recomputed every render, so an answer clears its question.
  const missing = [...new Set([...platforms].flatMap(p => missingFor(p)))]
  const noPhotos = !(outbound.imageUrls || []).length
  const needsPay = platforms.has('ss') || platforms.has('myhome')
  // A scheduled run is a phone job: nobody at the PC, so anything the portal or the extension
  // cannot do (core/reshare-readiness.mjs blockers) stops the plan here, not at run time.
  const blocked = [...new Set([...platforms].flatMap(p => blocksFor(p)))]
  const ready = platforms.size > 0 && !missing.length && !noPhotos && !blocked.length && (!needsPay || autoPay != null)

  const toggle = (set, setter, k) => setter(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n })

  async function save() {
    setMsg(null)
    if (!device) return setMsg({ err: t('sched_need_browser') })
    const chosen = [...platforms]
    if (!chosen.length) return
    if (!ready) return setMsg({ err: t('sched_answer_first') })
    const accounts = {}
    for (const p of chosen) {
      const a = device.portals?.[p]
      if (!a?.logged_in || !confirmed.has(p)) return setMsg({ err: `${PORTALS.find(x => x.key === p).label}: ${t('sched_confirm_account')}` })
      accounts[p] = { account_id: a.account_id || null, phone_tail: a.phone_tail || null, name: a.name || null }
    }
    const step = Math.max(1, +every || 1), last = end && end >= start ? end : start
    const dates = []
    for (let d = start; d <= last && dates.length < 60; d = addDays(d, step)) dates.push(d)
    setBusy(true)
    try {
      await createSharePlan({
        listing_id: listingId, property_id: propertyId || null, device_id: device.device_id,
        platforms: chosen, dates, repeat_mode: 'new', time_mode: mode,
        window_from: from, window_to: to, exact_time: exact, accounts,
        payload: { ...outbound, autoPay: autoPay === true },
      })
      setMsg({ ok: t('sched_saved') })
      setPlatforms(new Set()); setConfirmed(new Set())
      load()
    } catch (e) {
      setMsg({ err: e.message })
    } finally {
      setBusy(false)
    }
  }

  // Phone first (owner 2026-10-09: "improve mobile UI layout"): one column, whole rows are the tap
  // targets, labels sit above their boxes, and boxes are 16px so iOS does not zoom on focus.
  const row = 'flex items-start gap-2 rounded-lg border border-border px-3 py-2 cursor-pointer'
  const pick = on => `${row} ${on ? 'border-primary bg-primary/5' : ''}`
  const box = 'mt-0.5 h-4 w-4 shrink-0'
  return (
    <div className="mt-3 rounded-lg border border-border text-sm">
      <button type="button" className="flex w-full items-center gap-2 px-3 py-3 text-left font-medium"
        aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <span className="text-muted-foreground">{open ? '▾' : '▸'}</span>
        <span className="flex-1">{t('sched_title')}</span>
        {plans.some(isLive) && <span className="text-xs text-emerald-600">{plans.filter(isLive).length} {t('sched_active')}</span>}
      </button>
      {open && (
      <div className="space-y-4 border-t border-border px-3 pb-3 pt-3">

      <section className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">{t('sched_platforms')}</p>
        {PORTALS.map(p => {
          const a = device?.portals?.[p.key]
          const on = platforms.has(p.key)
          return (
            <div key={p.key} className="space-y-1">
              <label className={pick(on)}>
                <input type="checkbox" className={box} checked={on} onChange={() => toggle(platforms, setPlatforms, p.key)} />
                <span className="font-medium">{p.label}</span>
              </label>
              {on && (a?.logged_in
                ? <label className={`${pick(confirmed.has(p.key))} ml-6`}>
                    <input type="checkbox" className={box} checked={confirmed.has(p.key)} onChange={() => toggle(confirmed, setConfirmed, p.key)} />
                    <span className="min-w-0 [overflow-wrap:anywhere]">{t('sched_confirm_account')}: <b>{a.name || '—'}{a.phone_tail ? ` …${a.phone_tail}` : ''}</b></span>
                  </label>
                : <p className="ml-6 text-xs text-destructive">{t('sched_not_logged')}</p>)}
            </div>
          )
        })}
      </section>

      {(missing.length > 0 || noPhotos) && (
        <section className="space-y-2 rounded-lg border border-amber-500/50 bg-amber-500/10 p-3">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400">{t('sched_questions')}</p>
          {missing.map(renderField)}
          {noPhotos && <p className="text-xs text-destructive">{t('desk_share_need_photos')}</p>}
        </section>
      )}
      {blocked.length > 0 && (
        <section className="space-y-1">
          {blocked.map(m => <p key={m} className="text-xs text-destructive">{m}</p>)}
        </section>
      )}

      {needsPay && (
        <section className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">{t('sched_pay_q')}</p>
          <label className={pick(autoPay === true)}><input type="radio" className={box} name="sched-pay" checked={autoPay === true} onChange={() => setAutoPay(true)} /><span>{t('sched_pay_yes')}</span></label>
          <label className={pick(autoPay === false)}><input type="radio" className={box} name="sched-pay" checked={autoPay === false} onChange={() => setAutoPay(false)} /><span>{t('sched_pay_no')}</span></label>
        </section>
      )}

      <section className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">{t('sched_days')}</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <label className="col-span-2 sm:col-span-1 text-xs text-muted-foreground">{t('sched_start')}
            <input type="date" className={field} value={start} min={tbilisiToday()} onChange={e => setStart(e.target.value)} />
          </label>
          <label className="text-xs text-muted-foreground">{t('sched_every')} ({t('sched_days_unit')})
            <input type="number" inputMode="numeric" min="1" max="30" className={field} value={every} onChange={e => setEvery(e.target.value)} />
          </label>
          <label className="text-xs text-muted-foreground">{t('sched_end')}
            <input type="date" className={field} value={end < start ? start : end} min={start} onChange={e => setEnd(e.target.value)} />
          </label>
        </div>
      </section>

      <section className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">{t('sched_time')}</p>
        <label className={pick(mode === 'window')}><input type="radio" className={box} name="sched-mode" checked={mode === 'window'} onChange={() => setMode('window')} /><span>{t('sched_window')}</span></label>
        {mode === 'window' && (
          <div className="ml-6 grid grid-cols-2 gap-2">
            <input type="time" aria-label="from" className={field} value={from} onChange={e => setFrom(e.target.value)} />
            <input type="time" aria-label="to" className={field} value={to} onChange={e => setTo(e.target.value)} />
          </div>
        )}
        <label className={pick(mode === 'exact')}><input type="radio" className={box} name="sched-mode" checked={mode === 'exact'} onChange={() => setMode('exact')} /><span>{t('sched_exact')}</span></label>
        {mode === 'exact' && (
          <div className="ml-6 grid grid-cols-2 gap-2">
            <input type="time" aria-label="time" className={field} value={exact} onChange={e => setExact(e.target.value)} />
          </div>
        )}
      </section>

      <section className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">{t('sched_repeat')}</p>
        <label className={pick(true)}><input type="radio" className={box} checked readOnly /><span>{t('sched_repeat_new')}</span></label>
        <label className={`${row} opacity-50 cursor-not-allowed`}><input type="radio" className={box} disabled /><span>{t('sched_repeat_refresh')}</span></label>
      </section>

      <div className="space-y-2">
        <button type="button" className={btn('primary') + ' w-full sm:w-auto'} disabled={busy || !ready} onClick={save}>{t('sched_save')}</button>
        {msg?.ok && <p className="text-xs text-emerald-600">{msg.ok}</p>}
        {msg?.err && <p className="text-xs text-destructive">{msg.err}</p>}
      </div>

      {plans.length > 0 && (
        <section className="space-y-2 border-t border-border pt-3">
          <p className="text-xs font-medium text-muted-foreground">{t('sched_plans')}</p>
          {plans.map(pl => (
            <div key={pl.id} className={`rounded-lg border border-border p-2 text-xs space-y-1 ${!isLive(pl) ? 'opacity-60' : ''}`}>
              {(pl.share_plan_run || []).slice().sort((a, b) => a.planned_at.localeCompare(b.planned_at)).map(r => (
                <div key={r.id}>
                  <div className="flex items-baseline gap-2">
                    <span className="tabular-nums">{fmt(r.planned_at)}</span>
                    <span className="font-medium">{PORTALS.find(x => x.key === r.platform)?.label || r.platform}</span>
                    <span className={`ml-auto ${r.state === 'published' ? 'text-emerald-600' : ['error', 'missed'].includes(r.state) ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {t(`sched_state_${r.state}`)}
                    </span>
                  </div>
                  {r.link && <a href={r.link} target="_blank" rel="noreferrer" className="underline text-emerald-600">{t('desk_share_job_open')}</a>}
                  {r.message && r.state !== 'published' && <p className="text-muted-foreground [overflow-wrap:anywhere]">{r.message}</p>}
                </div>
              ))}
              {isLive(pl) && (
                <button type="button" className="mt-1 py-1 text-destructive underline"
                  onClick={() => cancelSharePlan(pl.id).then(load).catch(e => setMsg({ err: e.message }))}>{t('sched_cancel')}</button>
              )}
            </div>
          ))}
        </section>
      )}
      </div>
      )}
    </div>
  )
}
